/**
 * PURPOSE: Configurar el fixture del Centro de Programación y aplicar sus cambios (FTR-0028 WG3,
 *   §15 y D-26).
 * RESPONSIBILITIES: Los parámetros del torneo (equipos, tope por grupo, rondas, fase final), la
 *   vista previa determinista, la creación del borrador y el camino explícito cuando un cambio haría
 *   desaparecer filas.
 *
 *   DOS CAMINOS, A PROPÓSITO. `PUT /draft` es el camino SEGURO: crea, o aplica cambios que solo
 *   agregan y mueven. Si algo fuese a desaparecer responde `409 SYNC_REQUIRED` y entonces —y solo
 *   entonces— se pide el impacto y se aplica con el `summaryHash` de lo que el administrador vio.
 *   Saltarse ese rodeo sería aplicar a ciegas una pérdida de estructura.
 *
 *   LA PREVIEW NO PERSISTE NADA. `POST /generate` es exactamente lo que se guardaría; sirve para
 *   decidir antes de escribir, no para escribir en dos pasos.
 *
 *   LA FASE FINAL NACE VACÍA (D-26). La primera etapa no se rellena sola: quién juega los cuartos lo
 *   decide la clasificación, no el generador.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/SetupPanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiEye, FiSave } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import {
  generateFixturePreview,
  previewDraftSync,
} from "../../../../services/programmingCenterService.js";
import { programmingCenterErrorMessage } from "../../../../utils/programmingCenterErrors.js";
import SidePanel from "../components/SidePanel.jsx";

const FINAL_STAGES = [
  { value: "", label: "Sin fase final" },
  { value: "R16", label: "Desde octavos" },
  { value: "QF", label: "Desde cuartos" },
  { value: "SF", label: "Desde semifinales" },
  { value: "F", label: "Solo la final" },
];

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">{label}</span>
    {children}
    {hint ? <span className="text-[11px] font-bold text-slate-400">{hint}</span> : null}
  </label>
);

const Counter = ({ label, value, tone = "slate" }) => (
  <div
    className={`rounded-xl border px-3 py-2 ${
      tone === "green"
        ? "border-emerald-200 bg-emerald-50"
        : tone === "amber"
          ? "border-amber-200 bg-amber-50"
          : "border-slate-200 bg-slate-50"
    }`}
  >
    <p className="text-lg font-black text-slate-900">{value}</p>
    <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">{label}</p>
  </div>
);

const SetupPanel = ({ open, onClose }) => {
  const { draft, context, scope, actions, busy, version } = useProgrammingCenter();
  const current = draft?.draft || null;

  const [form, setForm] = useState({
    teamsTotal: "",
    maxPerGroup: "4",
    rounds: "1",
    finalStartStage: "",
    slotMinutes: "60",
    thirdPlace: true,
  });
  const [preview, setPreview] = useState(null);
  const [impact, setImpact] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setImpact(null);
    setForm({
      teamsTotal: String(
        current?.teams_total || context?.config?.nro_instituciones_participantes || ""
      ),
      maxPerGroup: String(current?.max_per_group || 4),
      rounds: String(current?.rounds || 1),
      finalStartStage: current?.final_start_stage || "",
      slotMinutes: String(current?.slot_minutes || 60),
      thirdPlace: true,
    });
  }, [open, current, context]);

  const config = useMemo(
    () => ({
      teamsTotal: Number(form.teamsTotal) || 0,
      maxPerGroup: Number(form.maxPerGroup) || 0,
      rounds: Number(form.rounds) || 1,
      finalStartStage: form.finalStartStage || null,
      slotMinutes: Number(form.slotMinutes) || 60,
      thirdPlace: form.thirdPlace,
    }),
    [form]
  );

  const runPreview = async () => {
    setWorking(true);
    setImpact(null);
    try {
      const data = await generateFixturePreview({ ...scope, config });
      setPreview(data);
    } catch (error) {
      setPreview(null);
      toast.error(programmingCenterErrorMessage(error, "Vista previa"));
    } finally {
      setWorking(false);
    }
  };

  /**
   * Guardar. Se intenta primero el camino seguro; solo si el backend avisa de que algo desaparecería
   * se pide el impacto, que es lo que después se aplicará con su hash.
   */
  const save = async () => {
    const result = await actions.saveDraftConfig(config);
    if (result.ok) {
      setImpact(null);
      onClose();
      return;
    }
    if (result.error?.code !== "SYNC_REQUIRED") return;

    setWorking(true);
    try {
      const data = await previewDraftSync({ ...scope, config });
      setImpact(data);
    } catch (error) {
      toast.error(programmingCenterErrorMessage(error, "Impacto"));
    } finally {
      setWorking(false);
    }
  };

  const applyImpact = async () => {
    if (!impact?.summary?.summaryHash) return;
    const result = await actions.applySync({
      config,
      summaryHash: impact.summary.summaryHash,
    });
    if (result.ok) {
      setImpact(null);
      onClose();
    }
  };

  const disabled = working || Boolean(busy);

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={current?.exists ? "Configuración del fixture" : "Armar fixture"}
      subtitle={
        current?.exists
          ? `Borrador versión ${version} · ${current.status === "materialized" ? "materializado" : "en diseño"}`
          : "Todavía no hay un fixture para este deporte"
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Equipos en total" hint="Instituciones que disputan la fase de grupos">
          <input
            type="number"
            min="2"
            value={form.teamsTotal}
            onChange={(event) => setForm((f) => ({ ...f, teamsTotal: event.target.value }))}
            className={inputClass}
          />
        </Field>
        <Field label="Máximo por grupo" hint="Entre 3 y 6; el reparto queda equilibrado">
          <input
            type="number"
            min="2"
            max="6"
            value={form.maxPerGroup}
            onChange={(event) => setForm((f) => ({ ...f, maxPerGroup: event.target.value }))}
            className={inputClass}
          />
        </Field>
        <Field label="Rondas" hint="2 = ida y vuelta, con local y visita invertidos">
          <select
            value={form.rounds}
            onChange={(event) => setForm((f) => ({ ...f, rounds: event.target.value }))}
            className={inputClass}
          >
            <option value="1">Una ronda</option>
            <option value="2">Ida y vuelta</option>
          </select>
        </Field>
        <Field label="Fase final" hint="La primera etapa nace vacía: se llena con la clasificación">
          <select
            value={form.finalStartStage}
            onChange={(event) => setForm((f) => ({ ...f, finalStartStage: event.target.value }))}
            className={inputClass}
          >
            {FINAL_STAGES.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Duración estimada (min)" hint="Solo para detectar cruces; no se guarda en el partido">
          <input
            type="number"
            min="10"
            step="5"
            value={form.slotMinutes}
            onChange={(event) => setForm((f) => ({ ...f, slotMinutes: event.target.value }))}
            className={inputClass}
          />
        </Field>
        {form.finalStartStage && form.finalStartStage !== "F" ? (
          <label className="flex items-center gap-2 self-end pb-2 text-sm font-bold text-slate-700">
            <input
              type="checkbox"
              checked={form.thirdPlace}
              onChange={(event) => setForm((f) => ({ ...f, thirdPlace: event.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
            />
            Incluir tercer puesto
          </label>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={runPreview}
          disabled={disabled || !config.teamsTotal}
          className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-black text-slate-700 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiEye className="text-sm" />
          Vista previa
        </button>
        <button
          type="button"
          onClick={save}
          disabled={disabled || !config.teamsTotal}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiSave className="text-sm" />
          {current?.exists ? "Aplicar cambios" : "Crear fixture"}
        </button>
      </div>

      {preview ? (
        <section className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <p className="mb-2 text-[11px] font-black uppercase tracking-wide text-slate-500">
            Vista previa · no se guardó nada
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Counter label="Grupos" value={preview.group_count} />
            <Counter label="Filas" value={preview.rows?.length ?? 0} />
            <Counter label="Partidos" value={preview.counts?.matches ?? 0} />
            <Counter label="BYE" value={preview.counts?.byes ?? 0} />
          </div>
          <p className="mt-2 text-xs font-bold text-slate-600">
            Reparto: {(preview.group_sizes || []).join(" · ")} equipos por grupo.
          </p>
        </section>
      ) : null}

      {impact ? (
        <section className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <p className="mb-2 flex items-center gap-2 text-sm font-black text-amber-800">
            <FiAlertTriangle className="text-sm" />
            Este cambio hace desaparecer filas existentes
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            <Counter label="Se mantienen" value={impact.summary?.keep ?? 0} tone="green" />
            <Counter label="Se mueven" value={impact.summary?.moved ?? 0} />
            <Counter label="Se crean" value={impact.summary?.create ?? 0} tone="green" />
            <Counter label="Huérfanas" value={impact.summary?.orphan ?? 0} tone="amber" />
            <Counter label="Bloqueadas" value={impact.summary?.blocked ?? 0} tone="amber" />
          </div>
          <p className="mt-2 text-xs font-bold text-amber-800">
            Las filas huérfanas no se borran: se marcan y conservan su programación por si su
            configuración vuelve. Las bloqueadas tienen actividad competitiva y no se tocan.
          </p>
          {impact.summary?.requiresReorganization ? (
            <p className="mt-2 rounded-lg border border-amber-400 bg-white px-2 py-1.5 text-xs font-black text-amber-800">
              Este cambio encoge grupos. Usa «Reorganizar grupos» para decidir qué pasa con los
              partidos ya materializados.
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={applyImpact}
              disabled={disabled}
              className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-black text-white transition hover:bg-amber-700 disabled:opacity-50"
            >
              Aplicar este impacto
            </button>
            <button
              type="button"
              onClick={() => setImpact(null)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-black text-slate-600"
            >
              Cancelar
            </button>
          </div>
        </section>
      ) : null}
    </SidePanel>
  );
};

export default SetupPanel;
